import React, { useState, useMemo, useCallback } from 'react';
import { AutoMovement } from '../../../../types';
import { useAutoMovement } from './useAutoMovement';
import { notify } from '../../../../utils/toast';
import { TrashIcon } from '../../../../components/icons';

export const useAutoMovementPage = () => {
    const {
        movements,
        isLoading,
        refreshMovements,
        createMovement,
        updateMovement,
        deleteMovement,
        duplicateMovement
    } = useAutoMovement();

    // Estado do formulário/painel
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [movementToEdit, setMovementToEdit] = useState<AutoMovement | undefined>(undefined);

    // Estado de busca e seleção
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Estado de modais de exclusão
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [movementToDelete, setMovementToDelete] = useState<AutoMovement | undefined>(undefined);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    // Filtragem
    const filteredMovements = useMemo(() => {
        if (!searchTerm) return movements;
        const term = searchTerm.toLowerCase();
        return movements.filter(m =>
            m.message.toLowerCase().includes(term) ||
            m.channelName?.toLowerCase().includes(term) ||
            m.stageName?.toLowerCase().includes(term)
        );
    }, [movements, searchTerm]);

    // Handlers de CRUD
    const handleOpenCreatePanel = useCallback(() => {
        setMovementToEdit(undefined);
        setIsPanelOpen(true);
    }, []);

    const handleOpenEditPanel = useCallback((movement: AutoMovement) => {
        setMovementToEdit(movement);
        setIsPanelOpen(true);
    }, []);

    const handleClosePanel = useCallback(() => {
        setIsPanelOpen(false);
        setMovementToEdit(undefined);
    }, []);

    const handleSave = useCallback(async (data: Omit<AutoMovement, 'id' | 'createdAt' | 'organizationId'>) => {
        try {
            if (movementToEdit) {
                await updateMovement(movementToEdit.id, data);
                notify.success('Movimentação atualizada com sucesso!');
            } else {
                await createMovement(data);
                notify.success('Movimentação criada com sucesso!');
            }
            handleClosePanel();
        } catch (error) {
            notify.error('Erro ao salvar movimentação.');
        }
    }, [movementToEdit, updateMovement, createMovement, handleClosePanel]);

    const handleOpenDeleteModal = useCallback((movement: AutoMovement) => {
        setMovementToDelete(movement);
        setIsDeleteModalOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (movementToDelete) {
            await deleteMovement(movementToDelete.id);
            notify.success('Movimentação excluída com sucesso!');
            setIsDeleteModalOpen(false);
            setMovementToDelete(undefined);
        }
    }, [movementToDelete, deleteMovement]);

    const handleDuplicate = useCallback(async (movement: AutoMovement) => {
        await duplicateMovement(movement);
        notify.success('Movimentação duplicada com sucesso!');
    }, [duplicateMovement]);

    const handleBulkDelete = useCallback(async () => {
        for (const id of selectedIds) {
            await deleteMovement(id);
        }
        notify.success(`${selectedIds.size} movimentação(ões) excluída(s) com sucesso!`);
        setSelectedIds(new Set());
        setIsBulkDeleteModalOpen(false);
    }, [selectedIds, deleteMovement]);

    const handleRefresh = useCallback(async () => {
        await refreshMovements();
        notify.success('Lista atualizada!');
    }, [refreshMovements]);

    // Seleção
    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const toggleAllSelection = useCallback((items: AutoMovement[]) => {
        if (selectedIds.size === items.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(items.map(i => i.id)));
        }
    }, [selectedIds.size]);

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    // Actions para menus
    const headerActions = useMemo(() => [
        {
            label: 'Excluir selecionados',
            icon: <TrashIcon className="w-4 h-4" />,
            onClick: () => setIsBulkDeleteModalOpen(true),
            variant: 'danger' as const,
            show: selectedIds.size > 0
        }
    ], [selectedIds.size]);

    const bulkActions = useMemo(() => [
        { label: 'Excluir', icon: TrashIcon, onClick: () => setIsBulkDeleteModalOpen(true), variant: 'danger' as const }
    ], []);

    return {
        // Data
        filteredMovements,
        isLoading,
        searchTerm,
        setSearchTerm,
        selectedIds,

        // Panel
        isPanelOpen,
        movementToEdit,
        handleOpenCreatePanel,
        handleOpenEditPanel,
        handleClosePanel,
        handleSave,

        // Delete modals
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        movementToDelete,
        handleOpenDeleteModal,
        handleConfirmDelete,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleBulkDelete,

        // Other actions
        handleDuplicate,
        handleRefresh,

        // Selection
        toggleSelection,
        toggleAllSelection,
        clearSelection,

        // Menu actions
        headerActions,
        bulkActions
    };
};
